import React, { useState, useEffect, useRef, useCallback } from 'react';
import { listFiles, searchFiles, getFileContent, connectDrive, checkConnection } from '../services/driveService';
import { toast } from 'sonner';
import { Search, Folder, FileText, FileSpreadsheet, File, Loader2, X, HardDrive, Cloud, AlertCircle, RefreshCw } from 'lucide-react';

const PROVIDERS = [
  { id: 'google', name: 'Google Drive', Icon: HardDrive, rootName: 'My Drive' },
  { id: 'microsoft', name: 'OneDrive', Icon: Cloud, rootName: 'OneDrive' },
];

const PROVIDER_STORAGE_KEY = 'wizardly_drive_provider';
const pathStorageKey = (provider) => `wizardly_drive_path_${provider}`;

function storageGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function storageSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
}

function readStoredPath(provider, rootCrumb) {
  try {
    const parsed = JSON.parse(storageGet(pathStorageKey(provider)) || 'null');
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0]?.id === 'root' &&
      parsed.every(c => c && typeof c.id === 'string' && typeof c.name === 'string')
    ) {
      return [rootCrumb, ...parsed.slice(1)];
    }
  } catch { /* corrupt entry — ignore */ }
  return [rootCrumb];
}

const GOOGLE_MIME_ICONS = {
  'application/vnd.google-apps.folder': { icon: Folder, color: 'text-yellow-500' },
  'application/vnd.google-apps.document': { icon: FileText, color: 'text-blue-500' },
  'application/vnd.google-apps.spreadsheet': { icon: FileSpreadsheet, color: 'text-green-500' },
  'application/vnd.google-apps.presentation': { icon: FileText, color: 'text-orange-500' },
  'application/pdf': { icon: FileText, color: 'text-red-500' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, color: 'text-blue-600' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileSpreadsheet, color: 'text-green-600' },
};

function getFileIcon(mimeType) {
  const entry = GOOGLE_MIME_ICONS[mimeType];
  if (entry) return entry;
  if (mimeType?.includes('folder')) return { icon: Folder, color: 'text-yellow-500' };
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return { icon: FileSpreadsheet, color: 'text-green-500' };
  if (mimeType?.includes('document') || mimeType?.includes('word')) return { icon: FileText, color: 'text-blue-500' };
  if (mimeType?.includes('pdf')) return { icon: FileText, color: 'text-red-500' };
  return { icon: File, color: 'text-muted-foreground' };
}

function isFolder(file) {
  return file.mimeType === 'application/vnd.google-apps.folder' ||
    file.mimeType === 'application/vnd.microsoft.folder' ||
    file.isFolder;
}

function ListSkeleton() {
  return (
    <div className="space-y-1" aria-hidden="true">
      {[0, 1, 2].map(i => (
        <div key={i} className="flex items-center gap-3 h-10 px-3">
          <div className="w-4 h-4 rounded bg-muted animate-pulse flex-shrink-0" />
          <div className="h-3 rounded bg-muted animate-pulse" style={{ width: `${72 - i * 16}%` }} />
        </div>
      ))}
    </div>
  );
}

function FileBrowser({ userId, provider, onFileSelected, onConnectionChange }) {
  const meta = PROVIDERS.find(p => p.id === provider);
  const rootName = meta.rootName;

  const [breadcrumbs, setBreadcrumbs] = useState(() => readStoredPath(provider, { id: 'root', name: meta.rootName }));
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingFile, setLoadingFile] = useState(null);
  const [nextToken, setNextToken] = useState(null); // next_page_token (Google) or next_link (Microsoft)
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const searchTimer = useRef(null);
  const activeSearchRef = useRef('');

  const currentFolder = breadcrumbs[breadcrumbs.length - 1];
  const searching = searchQuery.trim().length > 0;

  // Persist breadcrumb trail per provider so the picker reopens where the user left off
  useEffect(() => {
    storageSet(pathStorageKey(provider), JSON.stringify(breadcrumbs));
  }, [breadcrumbs, provider]);

  const loadFiles = useCallback(async (folderId, { fallbackToRoot = false } = {}) => {
    setLoading(true);
    setError(null);
    setNextToken(null);
    try {
      const data = await listFiles(userId, provider, { folderId, itemId: folderId });
      if (data.not_connected) {
        setNotConnected(true);
        onConnectionChange?.(provider, false);
        return;
      }
      if (data.error) throw new Error(data.error);
      setFiles(data.files || []);
      setNextToken(data.next_page_token || data.next_link || null);
      setNotConnected(false);
      onConnectionChange?.(provider, true);
    } catch (e) {
      // Remembered folder may have been moved/deleted — fall back to root once
      if (fallbackToRoot && folderId !== 'root') {
        setBreadcrumbs([{ id: 'root', name: rootName }]);
        await loadFiles('root');
        return;
      }
      setError(e.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [userId, provider, rootName, onConnectionChange]);

  // Initial load: restore the remembered folder, falling back to root if it fails
  useEffect(() => {
    loadFiles(breadcrumbs[breadcrumbs.length - 1].id, { fallbackToRoot: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFiles]);

  const runSearch = useCallback(async (query) => {
    setError(null);
    try {
      const data = await searchFiles(userId, provider, query);
      if (activeSearchRef.current !== query) return; // query changed or cleared while in flight
      if (data.not_connected) {
        setNotConnected(true);
        onConnectionChange?.(provider, false);
        return;
      }
      if (data.error) throw new Error(data.error);
      setSearchResults(data.files || []);
    } catch (e) {
      if (activeSearchRef.current !== query) return;
      setError(e.message || 'Search failed');
    }
  }, [userId, provider, onConnectionChange]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      activeSearchRef.current = '';
      setSearchResults(null);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      activeSearchRef.current = query;
      runSearch(query);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchQuery, runSearch]);

  const clearSearch = () => {
    activeSearchRef.current = '';
    setSearchQuery('');
    setSearchResults(null);
    setError(null);
  };

  const handleFolderClick = (folder) => {
    clearSearch();
    setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
    loadFiles(folder.id);
  };

  const handleBreadcrumb = (index) => {
    const crumb = breadcrumbs[index];
    clearSearch();
    setBreadcrumbs(prev => prev.slice(0, index + 1));
    loadFiles(crumb.id);
  };

  const handleLoadMore = async () => {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await listFiles(userId, provider, {
        folderId: currentFolder.id,
        itemId: currentFolder.id,
        pageToken: provider === 'google' ? nextToken : undefined,
        nextLink: provider === 'microsoft' ? nextToken : undefined,
      });
      if (data.not_connected) {
        setNotConnected(true);
        onConnectionChange?.(provider, false);
        return;
      }
      if (data.error) throw new Error(data.error);
      setFiles(prev => [...prev, ...(data.files || [])]);
      setNextToken(data.next_page_token || data.next_link || null);
    } catch (e) {
      toast.error(e.message || 'Failed to load more files');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRetry = () => {
    const query = searchQuery.trim();
    if (query) {
      activeSearchRef.current = query;
      runSearch(query);
    } else {
      loadFiles(currentFolder.id, { fallbackToRoot: true });
    }
  };

  const handleFileClick = async (file) => {
    if (isFolder(file)) { handleFolderClick(file); return; }
    setLoadingFile(file.id);
    try {
      const content = await getFileContent(userId, provider, file);
      if (content.error) throw new Error(content.error);
      onFileSelected({ ...content, file_name: file.name, provider });
    } catch (e) {
      toast.error(e.message || 'Failed to load file');
    } finally {
      setLoadingFile(null);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await connectDrive(userId, provider);
      toast.success(`${meta.name} connected`);
      setNotConnected(false);
      onConnectionChange?.(provider, true);
      loadFiles(currentFolder.id, { fallbackToRoot: true });
    } catch (e) {
      toast.error(e.message || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  if (notConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6">
        <meta.Icon className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium text-foreground">{meta.name} isn't connected</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          Read-only access to your files — we never modify or delete anything
        </p>
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="mt-2 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-accent text-accent-foreground hover:bg-accent/90 transition-colors disabled:opacity-60"
        >
          {connecting && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
          Connect {meta.name}
        </button>
      </div>
    );
  }

  const displayFiles = searching ? (searchResults || []) : files;
  const showSkeleton = loading || (searching && searchResults === null && !error);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="relative mb-3 flex-shrink-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={`Search ${meta.name}...`}
          aria-label={`Search ${meta.name}`}
          className="w-full pl-8 pr-3 py-1.5 text-sm rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {/* Search context or breadcrumbs */}
      {searching ? (
        <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground min-w-0 flex-shrink-0">
          <span className="truncate" title={searchQuery.trim()}>
            Results for &ldquo;{searchQuery.trim()}&rdquo; in {meta.name}
          </span>
          <button
            onClick={clearSearch}
            aria-label="Clear search and return to folder"
            className="p-0.5 rounded hover:bg-muted/60 hover:text-foreground transition-colors flex-shrink-0"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 mb-2 text-xs text-muted-foreground flex-wrap flex-shrink-0">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.id}>
              {i > 0 && <span aria-hidden="true">/</span>}
              <button
                onClick={() => handleBreadcrumb(i)}
                title={crumb.name}
                className={i === breadcrumbs.length - 1 ? 'font-medium text-foreground' : 'hover:text-foreground transition-colors'}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-y-auto space-y-0.5 min-h-0">
        {error ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 text-center px-4">
            <AlertCircle className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border text-foreground hover:bg-muted/60 transition-colors"
            >
              <RefreshCw className="w-3 h-3" aria-hidden="true" />
              Retry
            </button>
          </div>
        ) : showSkeleton ? (
          <ListSkeleton />
        ) : displayFiles.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            {searching ? 'No results found' : 'This folder is empty'}
          </div>
        ) : (
          <>
            {displayFiles.map(file => {
              const { icon: Icon, color } = getFileIcon(file.mimeType);
              const isLoadingThis = loadingFile === file.id;
              return (
                <button
                  key={file.id}
                  onClick={() => handleFileClick(file)}
                  disabled={!!loadingFile}
                  className="w-full flex items-center gap-3 px-3 h-10 rounded hover:bg-muted/60 transition-colors text-left disabled:opacity-60"
                >
                  {isLoadingThis
                    ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" aria-hidden="true" />
                    : <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} aria-hidden="true" />
                  }
                  <span className="text-sm text-foreground truncate flex-1" title={file.name}>{file.name}</span>
                  {file.modifiedTime && (
                    <span className="text-xs text-muted-foreground flex-shrink-0 text-right hidden sm:block">
                      {new Date(file.modifiedTime).toLocaleDateString()}
                    </span>
                  )}
                </button>
              );
            })}
            {!searching && nextToken && (
              <div className="pt-1 pb-2 flex justify-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors disabled:opacity-60"
                >
                  {loadingMore && <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function DriveFilePicker({ userId, onFileSelected, onClose, title = 'Import from cloud' }) {
  const [provider, setProvider] = useState(() => {
    const stored = storageGet(PROVIDER_STORAGE_KEY);
    return PROVIDERS.some(p => p.id === stored) ? stored : 'google';
  });
  const [connections, setConnections] = useState({ google: null, microsoft: null });

  // Remember last-used provider
  useEffect(() => {
    storageSet(PROVIDER_STORAGE_KEY, provider);
  }, [provider]);

  // Escape closes the modal
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Connection status dots for the tabs
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    PROVIDERS.forEach(p => {
      checkConnection(userId, p.id)
        .then(status => { if (!cancelled) setConnections(prev => ({ ...prev, [p.id]: !!status?.connected })); })
        .catch(() => { if (!cancelled) setConnections(prev => ({ ...prev, [p.id]: false })); });
    });
    return () => { cancelled = true; };
  }, [userId]);

  const handleConnectionChange = useCallback((p, connected) => {
    setConnections(prev => (prev[p] === connected ? prev : { ...prev, [p]: connected }));
  }, []);

  const handleFileSelected = (content) => {
    onFileSelected(content);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ height: 'min(560px, 85vh)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Source tabs */}
        <div className="flex items-center gap-1 px-4 pt-2 border-b border-border flex-shrink-0" role="tablist" aria-label="Cloud storage source">
          {PROVIDERS.map(p => {
            const active = provider === p.id;
            const connected = connections[p.id];
            return (
              <button
                key={p.id}
                role="tab"
                aria-selected={active}
                onClick={() => setProvider(p.id)}
                title={`${p.name} — ${connected ? 'Connected' : 'Not connected'}`}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-t-md border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-accent text-foreground font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
                }`}
              >
                <p.Icon className="w-4 h-4" aria-hidden="true" />
                {p.name}
                <span
                  className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        {/* Browser */}
        <div className="flex-1 overflow-hidden p-4 min-h-0">
          <FileBrowser
            key={provider}
            userId={userId}
            provider={provider}
            onFileSelected={handleFileSelected}
            onConnectionChange={handleConnectionChange}
          />
        </div>
      </div>
    </div>
  );
}
